import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  FlexColumn,
  DropDown,
  Dialog,
  Alert,
} from '@elementary/common-ui/components';
import { useForm, Controller } from 'react-hook-form';
import SensitiveInfoDisplay from 'settings/components/shared/SensitiveInfoDisplay';
import { useScim } from '../hooks/useScim';
import { CreateScimResponse, ScimSourceType } from '../types';
import { PROVISIONING_SOURCE_MAP } from './ProvisioningSource';

const Content = styled(FlexColumn)`
  gap: 24px;
  min-height: 95px;
  overflow-y: auto;
`;

type FormData = {
  source: ScimSourceType;
};

const sourceTypeOptions = Object.values(PROVISIONING_SOURCE_MAP);

const CreatedProvisioningConfig = ({
  token,
  provisioningUrl,
}: {
  token: string;
  provisioningUrl: string;
}) => (
  <FlexColumn sx={{ gap: 3 }}>
    <FlexColumn sx={{ gap: 1 }}>
      <Alert severity="info">
        Copy and save the token and URL below —{' '}
        <span style={{ fontWeight: 'bold' }}>
          they won&apos;t be shown again.
        </span>
      </Alert>
    </FlexColumn>

    <FlexColumn sx={{ gap: 2 }}>
      <SensitiveInfoDisplay label="Provisioning URL" value={provisioningUrl} />
      <SensitiveInfoDisplay label="Token" value={token} />
    </FlexColumn>
  </FlexColumn>
);
type Props = {
  open: boolean;
  onClose: () => void;
};

const ProvisioningConfigDialog = ({ open, onClose }: Props) => {
  const [createdConfig, setCreatedConfig] = useState<CreateScimResponse | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const scim = useScim();

  const defaultValues = useMemo(
    () => ({
      source: scim.data ? scim.data.source : ScimSourceType.OKTA,
    }),
    [scim.data],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setCreatedConfig(null);
      setErrorMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const mutateFunc = scim.data ? scim.recreate : scim.create;
        const result = await mutateFunc({ source: data.source });
        setCreatedConfig(result);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          'Failed to create provisioning configuration. Please try again.';
        setErrorMessage(errorMessage);
        return;
      }
    },
    [scim],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Provisioning Configuration"
      message={
        <Content>
          <Typography>
            Select the type of identity provider you want to configure for user
            provisioning.
          </Typography>
          <Controller
            name="source"
            control={control}
            rules={{ required: 'Source type is required' }}
            render={({ field }) => (
              <FlexColumn>
                <DropDown
                  {...field}
                  size="large"
                  options={sourceTypeOptions}
                  placeholder="Select provider type"
                  error={!!errors.source}
                  disabled={!!createdConfig}
                />
                {errors.source && (
                  <Typography sx={{ fontSize: 12, color: 'error.main' }}>
                    {errors.source.message}
                  </Typography>
                )}
              </FlexColumn>
            )}
          />
          {scim.data && !createdConfig && (
            <Alert severity="warning">
              This will delete the current configuration and create a new one.
            </Alert>
          )}
          {createdConfig && (
            <CreatedProvisioningConfig
              token={createdConfig.token}
              provisioningUrl={createdConfig.provisioning_url}
            />
          )}
          {errorMessage && <Alert severity="error" title={errorMessage} />}
        </Content>
      }
      confirmText={createdConfig ? 'Done' : scim.data ? 'Re-Create' : 'Create'}
      onConfirmButtonClick={createdConfig ? onClose : handleSubmit(onSubmit)}
      confirmDisabled={!isValid}
      isLoading={scim.isLoading}
      hideCancelButton={!!createdConfig}
      actionButtonText={scim.data ? 'Clear' : undefined}
      onActionButtonClick={async () => {
        await scim.delete();
        onClose();
      }}
      actionButtonDisabled={scim.isLoading}
      actionButtonColor="error"
      actionButtonVariant="contained"
    />
  );
};

export default ProvisioningConfigDialog;
